import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { hitlApi, Escalation } from '../services/hitlApi';
import { fetchCurrentUserId } from '../services/apiClient';
import { wsService } from '../services/websocket';
import { getSupportContext, SupportConversationContext } from '../services/supportApi';
import styles from './SupportRequests.module.css';

interface Identity { subject: string; token: string }
interface Props extends Identity { enabled: boolean; pending: boolean; failed: boolean; retry: () => unknown }
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
export function SupportRequests({ subject, token, enabled, pending, failed, retry }: Props) {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Escalation[]>([]);
  const [owner, setOwner] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [taking, setTaking] = useState<string | null>(null);
  const generation = useRef(0);
  const mounted = useRef(false);
  const identityKey = JSON.stringify([subject, token, enabled]);
  const scope = useRef({ key: identityKey, epoch: 0 });
  if (scope.current.key !== identityKey) scope.current = { key: identityKey, epoch: scope.current.epoch + 1 };
  const epoch = scope.current.epoch;
  const [rowsEpoch, setRowsEpoch] = useState(-1);
  const current = (expected: number) => mounted.current && scope.current.epoch === expected;
  const refresh = useCallback(async () => {
    const request = ++generation.current;
    if (!enabled || !subject || !token) return;
    const expected = epoch;
    setLoading(true); setError(false);
    try {
      const [requests, userId] = await Promise.all([hitlApi.listEscalations('all', token), fetchCurrentUserId(token)]);
      if (!Array.isArray(requests) || !UUID.test(userId) || requests.some(row => !row || !UUID.test(row.id) ||
        !UUID.test(row.learner_id) || !['waiting', 'assigned'].includes(row.status) ||
        (row.session_id && !UUID.test(row.session_id)))) throw new Error('Invalid queue');
      if (current(expected) && request === generation.current) { setRows(requests); setOwner(userId); setRowsEpoch(expected); }
    } catch { if (current(expected) && request === generation.current) { setRows([]); setRowsEpoch(expected); setError(true); } }
    finally { if (current(expected) && request === generation.current) setLoading(false); }
  // Scope epoch includes subject, bearer and availability (including ABA switches).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, subject, token, epoch]);
  useEffect(() => {
    mounted.current = true;
    setTaking(null); setRows([]); setOwner(''); setRowsEpoch(-1); setError(false);
    void refresh();
    if (!enabled) return () => { mounted.current = false; generation.current += 1; };
    const timer = setInterval(() => void refresh(), 15_000);
    const off = wsService.on('hitl:queue:updated', () => void refresh());
    return () => { mounted.current = false; generation.current += 1; clearInterval(timer); off(); };
  }, [enabled, refresh]);
  const take = async (id: string) => {
    if (taking || !enabled || error || rowsEpoch !== epoch || !UUID.test(id)) return;
    setTaking(id);
    const expected = epoch;
    try {
      const result = await hitlApi.takeEscalation(id, token);
      if (!UUID.test(result?.session?.id)) throw new Error('Invalid session');
      if (current(expected)) navigate(`/hitl/session/${encodeURIComponent(result.session.id)}`);
    } catch { if (current(expected)) { setError(true); setRows([]); } }
    finally { if (current(expected)) setTaking(null); }
  };
  return <section className={styles.requests} aria-label="Human support requests">
    <header><h2>Human requests</h2>{enabled && <Link to="/hitl">Full queue</Link>}</header>
    {pending ? <p role="status">Checking human support availability…</p>
      : failed ? <p role="alert">Availability could not be verified. <button type="button" onClick={() => retry()}>Retry availability</button></p>
      : !enabled ? <p>Human takeover is not enabled here. Ordinary messages remain available.</p>
      : error && rowsEpoch === epoch ? <p role="alert">Human requests could not be loaded. <button type="button" onClick={() => void refresh()}>Retry requests</button></p>
      : rowsEpoch !== epoch || (loading && !rows.length) ? <p role="status">Loading human requests…</p>
      : !rows.length ? <p>No open human requests in your teaching scope.</p>
      : <ul>{rows.map(row => <li key={row.id}>
        <span className={styles.badge}>{row.status === 'waiting' ? 'Waiting for instructor' : row.instructor_id === owner ? 'Assigned to you' : 'Assigned'}</span>
        <p>{row.learner_note || `Learner ${row.learner_id.slice(0, 8)} · requests human assistance`}</p>
        {row.status === 'waiting' ? <button type="button" disabled={Boolean(taking) || loading}
          onClick={() => void take(row.id)}>{taking === row.id ? 'Accepting…' : 'Accept request'}</button>
          : row.instructor_id === owner && row.session_id ? <Link to={`/hitl/session/${encodeURIComponent(row.session_id)}`}>Open human session</Link>
          : <span className={styles.muted}>Another instructor is helping.</span>}
      </li>)}</ul>}
  </section>;
}

export function SupportOrigin({ subject, token, threadId, canOpenSession }: Identity & { threadId: string | null; canOpenSession: boolean }) {
  const key = JSON.stringify([subject, token, threadId]);
  const scope = useRef({ key, epoch: 0 });
  if (scope.current.key !== key) scope.current = { key, epoch: scope.current.epoch + 1 };
  const epoch = scope.current.epoch;
  const [state, setState] = useState<{ epoch: number; context: SupportConversationContext | null; failed: boolean }>({ epoch: -1, context: null, failed: false });
  useEffect(() => {
    let active = true;
    setState({ epoch, context: null, failed: false });
    if (threadId && subject && token) void getSupportContext(threadId, token).then(context => {
      if (active && scope.current.epoch === epoch) setState({ epoch, context, failed: false }); })
      .catch(() => { if (active && scope.current.epoch === epoch) setState({ epoch, context: null, failed: true }); });
    return () => { active = false; };
  }, [threadId, subject, token, epoch]);
  if (state.epoch !== epoch || !threadId || !subject || !token) return null;
  if (state.failed) return <p className={styles.origin} role="status">Linked support context is unavailable. This conversation can still be used as an ordinary message thread.</p>;
  if (!state.context) return null;
  return <div className={styles.origin}>Follow-up after human instructor support. Ako is not paused by these messages.
    {canOpenSession && <Link to={`/hitl/session/${encodeURIComponent(state.context.humanSessionId)}`}>View linked human session</Link>}
  </div>;
}
